import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import {AgyleCheckListIcon} from '../../../../../components/icons/agyle';
import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import config from '../../../../../app/config';
import {dynamicWidth, patientSafetyChecklistSectionValidation} from '../../formsUtil';

const boxColors = {
    green: '#01AF69',
    yellow: '#FEB03A',
    red: '#DC4A5D',
    burgundy: '#A32626',
    blue: '#3C78D8',
};

const useStyles = makeStyles((theme) => {
    return {
        singleIconFlag: {
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            width: dynamicWidth,
            height: dynamicWidth,
            padding: `${(dynamicWidth - 24) / 2}px 0`,
            borderRadius: 4,
            '&.NOT_STARTED, &.EXPIRED': {
                backgroundColor: boxColors.red,
            },
            '&.OK': {
                backgroundColor: boxColors.green,
            },
            '&.TO_EXPIRED': {
                backgroundColor: boxColors.yellow,
            },
        },
    };
});

const STATUS = {
    NOT_ACTIVE: 'NOT_ACTIVE',
    NOT_STARTED: 'NOT_STARTED',
    OK: 'OK',
    EXPIRED: 'EXPIRED',
    TO_EXPIRED: 'TO_EXPIRED',
};

const PatientSafetyChecklistIconStatus = ({patient}) => {
    const {t} = useTranslation();
    const classes = useStyles();

    // Get offset time
    const offsetTime = config?.patientSafetyChecklist?.offsetTime || 5;

    // Status icon state
    const [tooltip, setTooltip] = useState('');
    const [type, setType] = useState(STATUS.NOT_ACTIVE);
    const [hours, setHours] = useState(0);
    const [minutesLeft, setMinutesLeft] = useState(0);

    // Check Patient Safety Checklist status
    useEffect(() => {
        let interval;
        // Check if Triage or See And Treat is started
        if (patient?.triage || patient?.studies?.seeAndTreat) {
            calculate();
            interval = setInterval(calculate, 10000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [patient]);

    const calculate = () => {
        const psc = patient?.studies?.safetyChecklist;

        if (!_.isNil(psc)) {
            const currentTriage = patient?.triage?.when;
            const seeAndTreat = patient?.studies?.seeAndTreat?.when;

            // Get the earliest time of form
            const dateToCheck = (seeAndTreat && (!currentTriage || moment(seeAndTreat).isAfter(moment(currentTriage))))
                ? seeAndTreat
                : currentTriage;
            const timeHour = moment().diff(moment(dateToCheck), 'hours');
            const timeMinute = moment().diff(moment(dateToCheck), 'minutes');

            const sections = [1, 2, 3, 4];
            const sectionValidationFlags = [false, false, false, false];

            // Init state
            let isExpired = false;
            let isOk = false;
            let minutesLeftToExpired = 0;
            let mainSectionValid = false;

            // Loop through sections and perform validation
            for (const section of sections) {
                sectionValidationFlags[section - 1] = Boolean(patientSafetyChecklistSectionValidation(psc, section, patient));
            }

            // Spaghetti Bolognese (╯=▃=)╯︵┻━┻
            for (const index of [0, 1, 2, 3]) {
                if (timeHour === index && (index !== 0 ? sectionValidationFlags[index - 1] : true)) {
                    mainSectionValid = true;
                    const expired = checkHourScrewedUp(index + 1, timeMinute, timeHour);
                    if (sectionValidationFlags[index]) {
                        isOk = true;
                    } else {
                        isExpired = expired;
                        if (!expired) {
                            const getTime = getOffsetTime(index + 1, true);
                            const diff = getTime - timeMinute;

                            if (diff <= offsetTime) {
                                isOk = false;
                                minutesLeftToExpired = diff;
                            } else {
                                isOk = true;
                            }
                        }
                    }
                }
            }

            // Check a dynamic group
            if (!mainSectionValid) {
                const lastDynamicSection = _.last(psc?.nextHours);
                if (lastDynamicSection) {
                    if (timeHour > lastDynamicSection?.counter - 1) {
                        isExpired = true;
                    } else if (patientSafetyChecklistSectionValidation(lastDynamicSection, 0)) {
                        isOk = true;
                    } else {
                        const expired = checkHourScrewedUp(lastDynamicSection.counter - 1, timeMinute, timeHour);
                        isExpired = expired;
                        if (!expired) {
                            const getTime = getOffsetTime(lastDynamicSection.counter - 1, true);
                            const diff = getTime - timeMinute;
                            if (diff <= offsetTime) {
                                isOk = false;
                                minutesLeftToExpired = diff;
                            } else {
                                isOk = true;
                            }
                        }
                    }
                } else {
                    isExpired = true;
                }
            }

            if (isOk && !isExpired) {
                setType(STATUS.OK);
            } else if (!isOk && !isExpired) {
                setType(STATUS.TO_EXPIRED);
                setMinutesLeft(minutesLeftToExpired);
            } else if (isExpired) {
                setType(STATUS.EXPIRED);
            }
            setHours(timeHour);
        } else {
            setType(STATUS.NOT_STARTED);
        }
    };

    const getOffsetTime = (hour, withoutOffset) => {
        return ((60 * hour) - (withoutOffset ? 0 : Number(offsetTime)));
    };

    const checkHourScrewedUp = (hour, timesInMin, timesInHour) => {
        return timesInMin >= getOffsetTime(hour, true);
    };

    // Set tooltip by status
    useEffect(() => {
        switch (type) {
            case STATUS.OK:
                setTooltip('');
                break;
            case STATUS.TO_EXPIRED:
                setTooltip(`You have '${minutesLeft}' minutes left to complete the safety checklist for appropriate hour`);
                break;
            case STATUS.EXPIRED:
                setTooltip(`Time has expired. Please complete the safety checklist for the appropriate hour. Hours from completing triage ${hours}`);
                break;
            case STATUS.NOT_STARTED:
                setTooltip(t('Safety Checklist has not been started!'));
                break;
            default:
                setTooltip('');
        }
    }, [type, minutesLeft]);

    // Don't show icon if not started
    if (type === STATUS.NOT_ACTIVE) return null;

    return (
        <Tooltip title={tooltip}>
            <div className={clsx(classes.singleIconFlag, type)}>
                <AgyleCheckListIcon/>
            </div>
        </Tooltip>
    );
};

export default PatientSafetyChecklistIconStatus;